import { pricing } from "@/data/pricing";

export type Category = {
  id: string;
  label: string;
};

export type ProductSize = {
  id: string;
  label: string;
  delta: number;
};

export type ProductAddon = {
  id: string;
  label: string;
  price: number;
};

export type ProductNutrition = {
  serving: string;
  calories: number;
  protein: number;
  fat: number;
  carbs: number;
};

export type Product = {
  id: string;
  name: string;
  category: string;
  description: string;
  basePrice: number;
  image: string;
  badge?: string;
  sizes?: ProductSize[];
  addons?: ProductAddon[];

  ingredients?: string;
  allergens?: string[];
  nutrition?: ProductNutrition;
};

// COFFEETRACK_POINTS_SINGLE_SOURCE_V319
//
// Pickup locations live only in src/data/points.ts.
// Catalog contains menu/pricing data only.
//

export const categories: Category[] = [
  { id: "popular", label: "Популярное" },
  { id: "coffee", label: "Кофе" },
  { id: "other", label: "Не кофе" },
  { id: "cold", label: "Холодные" },
  { id: "food", label: "Еда" },
];

const coffeeSizes: ProductSize[] = [
  {
    id: "300",
    label: "300 мл",
    delta: pricing.sizes.coffee["300"],
  },
  {
    id: "400",
    label: "400 мл",
    delta: pricing.sizes.coffee["400"],
  },
];

const largeDrinkSizes: ProductSize[] = [
  {
    id: "400",
    label: "400 мл",
    delta: pricing.sizes.largeDrink["400"],
  },
  {
    id: "500",
    label: "500 мл",
    delta: pricing.sizes.largeDrink["500"],
  },
];

const coffeeAddons: ProductAddon[] = [
  {
    id: "syrup",
    label: "Сироп",
    price: pricing.addons.coffee.syrup,
  },
  {
    id: "alt-milk",
    label: "Альтернативное молоко",
    price: pricing.addons.coffee["alt-milk"],
  },
  {
    id: "extra-shot",
    label: "Доп. эспрессо",
    price: pricing.addons.coffee["extra-shot"],
  },
];

const hotdogAddons: ProductAddon[] = [
  {
    id: "cheese-sauce",
    label: "Сырный соус",
    price: pricing.addons.hotdog["cheese-sauce"],
  },
  {
    id: "jalapeno",
    label: "Халапеньо",
    price: pricing.addons.hotdog.jalapeno,
  },
  {
    id: "beef",
    label: "Говяжья сосиска",
    price: pricing.addons.hotdog.beef,
  },
];

//
// COFFEETRACK_CATALOG_USES_PRICING_V320
//
// Денежные значения находятся в src/data/pricing.ts.
// Сейчас pricingStatus === "demo".
// Перед production подставляем настоящий прайс владельца.
//
export const products: Product[] = [
  // ─────────────────────────────
  // КОФЕ
  // ─────────────────────────────

  {
    id: "cappuccino",
    name: "Капучино",
    category: "coffee",
    description:
      "Эспрессо, молоко и плотная молочная пенка",
    basePrice: pricing.products["cappuccino"],
    image: "/products/cappuccino.svg",
    badge: "Хит",
    sizes: coffeeSizes,
    addons: coffeeAddons,
  },

  {
    id: "latte",
    name: "Латте",
    category: "coffee",
    description:
      "Мягкий кофейный вкус и больше молока",
    basePrice: pricing.products["latte"],
    image: "/products/latte.svg",
    sizes: coffeeSizes,
    addons: coffeeAddons,
  },

  {
    id: "americano",
    name: "Американо",
    category: "coffee",
    description:
      "Классический чёрный кофе на основе эспрессо",
    basePrice: pricing.products["americano"],
    image: "/products/americano.svg",
    sizes: coffeeSizes,
    addons: [
      {
        id: "syrup",
        label: "Сироп",
        price: pricing.addons.americano.syrup,
      },
      {
        id: "extra-shot",
        label: "Доп. эспрессо",
        price: pricing.addons.americano["extra-shot"],
      },
    ],
  },

  {
    id: "raf",
    name: "Раф",
    category: "coffee",
    description:
      "Сливочный кофе с нежной текстурой",
    basePrice: pricing.products["raf"],
    image: "/products/raf.svg",
    badge: "Советуем",
    sizes: coffeeSizes,
    addons: coffeeAddons,
  },

  // ─────────────────────────────
  // НЕ КОФЕ
  // ─────────────────────────────

  {
    id: "cocoa",
    name: "Какао",
    category: "other",
    description:
      "Горячее какао на молоке",
    basePrice: pricing.products["cocoa"],
    image: "/products/cocoa.svg",
    sizes: coffeeSizes,
    addons: [
      {
        id: "syrup",
        label: "Сироп",
        price: pricing.addons.cocoa.syrup,
      },
      {
        id: "alt-milk",
        label: "Альтернативное молоко",
        price: pricing.addons.cocoa["alt-milk"],
      },
    ],
  },

  {
    id: "tea",
    name: "Чай",
    category: "other",
    description:
      "Горячий ароматный чай",
    basePrice: pricing.products["tea"],
    image: "/products/tea.svg",
    sizes: [
      {
        id: "400",
        label: "400 мл",
        delta: pricing.sizes.tea["400"],
      },
      {
        id: "500",
        label: "500 мл",
        delta: pricing.sizes.tea["500"],
      },
    ],
  },

  // ─────────────────────────────
  // ХОЛОДНЫЕ
  // ─────────────────────────────

  {
    id: "bumble",
    name: "Бамбл кофе",
    category: "cold",
    description:
      "Эспрессо, цитрусовый сок и лёд",
    basePrice: pricing.products["bumble"],
    image: "/products/bumble.svg",
    badge: "Топ",
    sizes: largeDrinkSizes,
  },

  {
    id: "iced-latte",
    name: "Айс-латте",
    category: "cold",
    description:
      "Эспрессо, холодное молоко и лёд",
    basePrice: pricing.products["iced-latte"],
    image: "/products/iced-latte.svg",
    sizes: largeDrinkSizes,
    addons: coffeeAddons,
  },

  {
    id: "lemonade",
    name: "Лимонад",
    category: "cold",
    description:
      "Освежающий фирменный лимонад со льдом",
    basePrice: pricing.products["lemonade"],
    image: "/products/lemonade.svg",
    badge: "Лето",
    sizes: largeDrinkSizes,
  },

  {
    id: "smoothie",
    name: "Смузи",
    category: "cold",
    description:
      "Холодный фруктово-ягодный напиток",
    basePrice: pricing.products["smoothie"],
    image: "/products/smoothie.svg",
    sizes: largeDrinkSizes,
  },

  {
    id: "juice-bowl",
    name: "Джус-болл",
    category: "cold",
    description:
      "Фирменный фруктовый напиток CoffeeTrack",
    basePrice: pricing.products["juice-bowl"],
    image: "/products/juice-bowl.svg",
    badge: "Фирменный",
  },

  // ─────────────────────────────
  // ЕДА
  // ─────────────────────────────

  {
    id: "hotdog",
    name: "Хот-дог",
    category: "food",
    description:
      "Сосиска, мягкая булочка, овощи и соусы",
    basePrice: pricing.products["hotdog"],
    image: "/products/hotdog.svg",
    badge: "Хит",
    addons: hotdogAddons,
  },

  {
    id: "frenchdog",
    name: "Френч-дог",
    category: "food",
    description:
      "Горячая сосиска в хрустящей французской булочке",
    basePrice: pricing.products["frenchdog"],
    image: "/products/frenchdog.svg",
    addons: hotdogAddons,
  },

  {
    id: "corndog",
    name: "Корн-дог",
    category: "food",
    description:
      "Сосиска в хрустящем кукурузном тесте",
    basePrice: pricing.products["corndog"],
    image: "/products/corndog.svg",
    badge: "Топ",
  },

  {
    id: "fries",
    name: "Картофель фри",
    category: "food",
    description:
      "Золотистый хрустящий картофель",
    basePrice: pricing.products["fries"],
    image: "/products/fries.svg",
    sizes: [
      {
        id: "standard",
        label: "Стандарт",
        delta: pricing.sizes.fries.standard,
      },
      {
        id: "large",
        label: "Большой",
        delta: pricing.sizes.fries.large,
      },
    ],
  },

  {
    id: "nuggets",
    name: "Наггетсы",
    category: "food",
    description:
      "Хрустящие куриные наггетсы",
    basePrice: pricing.products["nuggets"],
    image: "/products/nuggets.svg",
    sizes: [
      {
        id: "6",
        label: "6 шт.",
        delta: pricing.sizes.nuggets["6"],
      },
      {
        id: "9",
        label: "9 шт.",
        delta: pricing.sizes.nuggets["9"],
      },
    ],
  },

  {
    id: "cheese-sticks",
    name: "Сырные палочки",
    category: "food",
    description:
      "Горячие сырные палочки в хрустящей панировке",
    basePrice: pricing.products["cheese-sticks"],
    image: "/products/cheese-sticks.svg",
    badge: "Советуем",
  },

  {
    id: "shrimp",
    name: "Креветки в кляре",
    category: "food",
    description:
      "Креветки в хрустящем золотистом кляре",
    basePrice: pricing.products["shrimp"],
    image: "/products/shrimp.svg",
  },
];

// COFFEETRACK_POPULAR_REAL_PHOTOS_V318
//
// Keep the main Popular section photo-backed.
// Products without final photography stay available
// in their normal categories.
//
export const popularIds = [
  "cappuccino",
  "raf",
  "bumble",
  "hotdog",
  "corndog",
  "cheese-sticks",
  "fries",
  "nuggets",
];
